import { resetPassword } from "api/auth";
import { ApiError } from "api/client";
import { Button, Card } from "components";
import { Form, Formik, type FormikHelpers } from "formik";
import AccountsContainer from "pages/accounts-onboarding/components/accounts-container";
import type { ResetPasswordModel } from "pages/accounts-onboarding/models";
import { createResetPasswordSchema } from "pages/accounts-onboarding/schema";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Paths from "routes/Paths";

import ResetPasswordForm from "./components";

import styles from "./ResetPassword.module.css";

type ResetPasswordPageProps = {
	initialValues: ResetPasswordModel;
};

export const ResetPasswordPage = ({ initialValues }: ResetPasswordPageProps) => {
	const [params] = useSearchParams();
	const token = params.get("token") ?? "";
	const navigate = useNavigate();
	const [isDone, setIsDone] = useState(false);
	const { t } = useTranslation("accountsPage");
	const validationSchema = useMemo(() => createResetPasswordSchema(t), [t]);

	const onFormSubmit = useCallback(
		async (values: ResetPasswordModel, formikHelpers: FormikHelpers<ResetPasswordModel>): Promise<void> => {
			if (!token) {
				formikHelpers.setStatus(t("resetPassword.missingToken"));

				formikHelpers.setSubmitting(false);

				return;
			}

			try {
				await resetPassword({ token, newPassword: values.newPassword });
				setIsDone(true);
			} catch (error) {
				formikHelpers.setStatus(error instanceof ApiError ? error.message : t("resetPassword.error"));
				formikHelpers.setSubmitting(false);
			}
		},
		[t, token]
	);

	const onSubmit = useCallback(() => navigate(Paths.signIn, { replace: true }), [navigate]);

	if (isDone) {
		return (
			<AccountsContainer>
				<Card className={styles.card}>
					<div className={styles.form}>
						<header className={styles.header}>
							<h1 className={styles.title}>{t("resetPassword.doneTitle")}</h1>
							<p className={styles.subtitle}>{t("resetPassword.doneSubtitle")}</p>
						</header>
						<footer className={styles.footer}>
							<Button className={styles.submitButton} onClick={onSubmit} type="button" fullWidth>
								{t("resetPassword.doneCta")}
							</Button>
						</footer>
					</div>
				</Card>
			</AccountsContainer>
		);
	}

	return (
		<Formik initialValues={initialValues} onSubmit={onFormSubmit} validationSchema={validationSchema}>
			<Form className={styles.page} noValidate>
				<AccountsContainer>
					<ResetPasswordForm />
				</AccountsContainer>
			</Form>
		</Formik>
	);
};
